import React, { FormEvent, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckIcon, MailIcon, UserIcon } from "lucide-react";
import { TextField } from "../../components/ui/TextField";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { Card } from "../../components/ui/Card";
import { spring } from "../../utils/motion";
const topics = ["General question", "Billing", "Schools & teams", "Press", "Bug report"];
export function Contact() {
  const reduce = useReducedMotion() ?? false;
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: topics[0],
    message: ""
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Tell us what to call you.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "That email doesn't look right.";
    if (form.message.trim().length < 10) next.message = "A little more detail helps (10+ characters).";
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus("sending");
    window.setTimeout(() => setStatus("sent"), 1100);
  };
  return <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 md:grid-cols-[1fr_1.2fr] md:px-8 md:py-24" data-id="element-1753" data-mp="bg442xwo80xz">
      <div data-id="element-1754" data-mp="3swvcwxs2vvhs">
        <h1 className="text-[clamp(38px,6vw,64px)] font-bold leading-[1.05] tracking-[-0.02em]" data-id="element-1755" data-mp="3sw3uyprelw9z">Say hola. Or ciao. Or hi.</h1>
        <p className="mt-5 max-w-[42ch] text-[17px] leading-relaxed text-ink-2" data-id="element-1756" data-mp="1ku0w6ujdnim">We read every message and usually reply within a working day, in whichever language you write.</p>
        <dl className="mt-10 space-y-5" data-id="element-1757" data-mp="3nytvehaaw48f">
          {[["Support", "help@polylexa.com"], ["Schools & partnerships", "schools@polylexa.com"], ["Press", "press@polylexa.com"]].map(([k, v]) => <div key={k} data-id="element-1758" data-mp="1khf73r0dx4kc">
              <dt className="text-sm font-bold text-ink-2" data-id="element-1759" data-mp="1kip6qkmvykt3">{k}</dt>
              <dd data-id="element-1760" data-mp="1fr765xorym8n">
                <a href={`mailto:${v}`} className="focus-ring rounded-md text-lg font-semibold text-ink underline decoration-2 underline-offset-4 hover:decoration-brand" data-id="element-1761" data-mp="1fqp0kag82vo2">
                  {v}
                </a>
              </dd>
            </div>)}
        </dl>
      </div>

      <Card className="p-6 md:p-8" data-id="element-1762" data-mp="23g9kavk79wye">
        <AnimatePresence mode="wait" initial={false} data-id="element-1763" data-mp="23f8asviu1iis">
          {status === "sent" ? <motion.div key="sent" initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={spring.smooth} className="py-10 text-center" role="status" data-id="element-1764" data-mp="1zfzi7tzj3qot">
              <motion.span initial={reduce ? false : {
            scale: 0.6,
            rotate: -10
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={spring.bouncy} className="mx-auto grid h-16 w-16 place-items-center rounded-full border-3 border-line bg-good text-night" data-id="element-1765" data-mp="j1hduz3vagik">
                <CheckIcon className="h-8 w-8" strokeWidth={3} data-id="element-1766" data-mp="8un6u7ytw7ch" />
              </motion.span>
              <h2 className="mt-5 text-2xl font-bold" data-id="element-1767" data-mp="dujc0aj9i4tm">Message sent</h2>
              <p className="mt-2 text-[15px] text-ink-2" data-id="element-1768" data-mp="inm4efcaz888">Thanks, {form.name.split(" ")[0]}. We'll reply to {form.email}.</p>
              <button type="button" onClick={() => {
            setForm({
              name: "",
              email: "",
              topic: topics[0],
              message: ""
            });
            setStatus("idle");
          }} className="focus-ring mt-6 rounded-lg text-sm font-semibold text-ink underline underline-offset-4" data-id="element-1769" data-mp="mnwcpynkiaq9">
                Send another
              </button>
            </motion.div> : <motion.form key="form" onSubmit={submit} noValidate initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="space-y-5" data-id="element-1770" data-mp="33c9whqmmoj3d">
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-1771" data-mp="xqm0fkkzdqq5">
                <TextField label="Name" icon={UserIcon} value={form.name} onValueChange={v => setForm({
              ...form,
              name: v
            })} error={errors.name} autoComplete="name" data-id="element-1772" data-mp="sod3ye8wg6ja" />
                <TextField label="Email" type="email" icon={MailIcon} value={form.email} onValueChange={v => setForm({
              ...form,
              email: v
            })} error={errors.email} autoComplete="email" data-id="element-1773" data-mp="11qcm59vnhgnt" />
              </div>
              <label className="block" data-id="element-1774" data-mp="9z6pj3f5213a">
                <span className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-1775" data-mp="a0hwtd7slqz5">Topic</span>
                <select value={form.topic} onChange={e => setForm({
              ...form,
              topic: e.target.value
            })} className="focus-ring w-full rounded-xl border-3 border-line bg-surface px-3.5 py-3 text-[15px] text-ink" data-id="element-1776" data-mp="nr3if9omsf0q">
                  {topics.map(t => <option key={t} data-id="element-1777" data-mp="3mg225q83b8i3">{t}</option>)}
                </select>
              </label>
              <TextField label="Message" multiline rows={5} value={form.message} onValueChange={v => setForm({
            ...form,
            message: v
          })} error={errors.message} placeholder="What's on your mind?" data-id="element-1778" data-mp="21wge1uhey4np" />
              <BrutalButton type="submit" full size="lg" loading={status === "sending"} data-id="element-1779" data-mp="2lmjt3odsgctx">
                {status === "sending" ? "Sending…" : "Send message"}
              </BrutalButton>
            </motion.form>}
        </AnimatePresence>
      </Card>
    </div>;
}